/* global Ecwid */
import { useContext, useRef, useState, useEffect, useCallback } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { useParams } from 'react-router-dom'
import classNames from 'classnames'
import CreateJs from 'preload-js'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import { Context as GeneralContext } from '@/context'
import ProductItem from '@/components/ProductItem'
import Footer from '@/components/Footer'
import { Context as LoadingContext } from '@/context/loading'
import BtnLink from '@/components/BtnLink'
import Meta from '@/components/Meta'
import { externalPreload } from '@/utils/dom'
import actions from '@/actions'
import constants from '@/constants'
import { decodeEntities } from '@/utils/decodeEntities'
import style from './style'

const useStyles = createUseStyles(style)

const Product = ({
  cpt: cptFromProps,
}) => {
  const { getImageUrl, headerHeight, setBackButtonVisible, setProductPage } = useContext(GeneralContext)
  const { setReady } = useContext(LoadingContext)
  const classes = useStyles({ headerHeight })
  const preload = useRef(new CreateJs.LoadQueue())
  const { stub } = useParams()
  const [load, setLoad] = useState(false)
  const [product, setProduct] = useState({})
  const [relatedProduct, setRelatedProduct] = useState([])
  const [availability, setAvailability] = useState(true)
  const [preorder, setPreorder] = useState(false)
  const [unlimited, setUnlimited] = useState(false)
  const [designerName, setDesignerName] = useState('')

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, strings, pages, mediaQueryKey, categories } = useSelector((state) => ({
    current: state.page.current,
    page: state.page.pages[stub] || {},
    strings: state.language.string,
    categories: state.shop.categories || [],
    pages: state.page.pages || {},
    mediaQueryKey: state.mediaQuery.key,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getSingleProduct = useCallback((cpt, index) => dispatch(actions.page.getDataCPT(cpt, index)), [dispatch])
  const getEcwidProduct = useCallback((id) => dispatch(actions.shop.getEcwidProduct(id)), [dispatch])
  const getEcwidRelatedProduct = useCallback((ids) => dispatch(actions.shop.getEcwidRelatedProduct(ids)), [dispatch])
  const setStoreProducts = useCallback((items) => dispatch(actions.shop.setStoreProducts(items)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    externalPreload(setLoad)
    setReady(true)
  }

  /*------------------------------
  Preload Media
  ------------------------------*/
  const preloadMedia = () => {
    preload.current.on('complete', completePreload)
    const heroMedia = getImageUrl(page.featured_media, 'full', stub, 'pages')
    preload.current.loadManifest(heroMedia)
  }

  /*------------------------------
  Preload Media when there are Data of Page in Redux
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0 && Object.keys(pages).length > 0) {
      const designer = Object.values(pages).find((p) => p.id === parseInt(Object.keys(page.related_designer_id)[0], 10))
      setDesignerName(designer?.title?.rendered)
    }
  }, [page, pages])

  useEffect(() => {
    if (Object.keys(page).length > 0) {
      preloadMedia()
      if (page.ecwid_prodotto_id) {
        getEcwidProduct(page.ecwid_prodotto_id)
          .then((data) => {
            setProduct({
              ...data,
              title: data.name,
              designer: categories?.find((cat) => cat.id === data.categoryIds[0])?.name,
              image: data.originalImageUrl,
              price: data.defaultDisplayedPriceFormatted,
              discountPrice: data.compareToPriceFormatted,
              isInStock: data.inStock,
            })

            if (data.relatedProducts.productIds.length > 0) {
              getEcwidRelatedProduct(data.relatedProducts.productIds.join(','))
                .then((res) => {
                  setRelatedProduct(res.map((r) => ({
                    ...r,
                    title: r.name,
                    designer: categories?.find((cat) => cat.id === r.categoryIds[0])?.name,
                    image: r.originalImageUrl,
                    price: r.defaultDisplayedPriceFormatted,
                    discountPrice: r.compareToPriceFormatted,
                    isInStock: r.inStock,
                  })))
                })
            }
          })
      }
    }
  }, [page])

  useEffect(() => {
    if (Object.keys(product).length > 0) setStoreProducts([product])
  }, [product])

  useEffect(() => {
    if (relatedProduct.length > 0) setStoreProducts(relatedProduct)
  }, [relatedProduct])

  /*------------------------------
  Preorder
  ------------------------------*/
  useEffect(() => {
    if (product.attributes?.length > 0) {
      const isPreordine = product.attributes.some((attr) => attr.name === 'Preordine')
      setPreorder(isPreordine)
    }
  }, [product])

  /*------------------------------
  Unlimited
  ------------------------------*/
  useEffect(() => {
    if (product.unlimited) setUnlimited(product.unlimited)
  }, [product])

  /*------------------------------
  Initialize
  ------------------------------*/
  const initialize = () => {
    if (current !== stub) setCurrentPage(stub, 'pages')
    getSingleProduct(cptFromProps, stub)
  }

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
    setProductPage(true)
    return () => setProductPage(false)
  }, [])

  /*------------------------------
  Setting del Back Button
  ------------------------------*/
  useEffect(() => {
    if (load) setBackButtonVisible(true)
  }, [load])

  /*------------------------------
  Check Availability in Cart
  ------------------------------*/
  const checkAvailability = (cart) => {
    let available = product.quantity > 0
    const itemInCart = cart.items.find((prod) => {
      return prod.product.id === product.id
    })
    if (itemInCart) {
      if (product.quantity <= itemInCart.quantity) {
        available = false
      }
    }
    setAvailability(available)
  }

  useEffect(() => {
    if (!product) return
    Ecwid?.OnCartChanged.add((cart) => {
      checkAvailability(cart)
    })
    setTimeout(() => {
      Ecwid?.Cart.get((cart) => {
        checkAvailability(cart)
      })
    }, 1000)
  }, [product])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta
        title={page.yoast_meta.find((y) => y.property?.includes('title')).content || page.title.rendered}
        meta={page.yoast_meta}
        schema={page.yoast_json_ld}
      />
    )
  }

  const handleAddToCart = (id, quantity) => {
    if (Ecwid) Ecwid.Cart.addProduct({ id, quantity })
  }

  const handleGoToCheckout = () => {
    if (Ecwid) Ecwid.Cart.gotoCheckout()
  }

  /*------------------------------
  Render Contols
  ------------------------------*/
  const renderControls = () => {
    const activeLanguage = window.location.pathname.split('/')[1]

    return (
      <div className={classes.controls}>
        <div className={classes.price}>
          {product.price}
          {product.discountPrice ? (
            <span className={classes.discountPrice}>
              {' '}
              {product.discountPrice}
            </span>
          ) : null}
        </div>
        <div className={classes.btnControls}>
          <BtnLink
            className={classes.btnCta}
            onClick={() => handleAddToCart(product.id, 1)}
            label={(preorder && !availability) || (preorder && unlimited) ? strings.preorder : strings.addtocart_label}
            theme="dark"
          />
          {(preorder && !availability) || (preorder && unlimited) ? (
            <div className={classes.preorder}>
              {product.attributes.find((attr) => attr.name === 'Preordine')?.valueTranslated[activeLanguage]}
            </div>
          ) : null}
          <BtnLink
            onClick={handleGoToCheckout}
            label={strings.checkout_label}
            theme="green"
          />
          {/* {(!preorder && !btnCartVisibile) && (
          <span>{strings.out_of_stock_label}</span>
          )} */}
        </div>
      </div>
    )
  }

  /*------------------------------
  Render Product
  ------------------------------*/
  const renderProduct = () => {
    return (
      <div className={classes.grid}>
        <div className={classes.gridContent}>
          <div className={classes.gridContentMain}>
            <div className={classes.gridContentMainContainer}>
              <div className={classes.gridContentMainTable}>
                <div className={classes.designer}>{designerName}</div>
                <div className={classes.title}>{decodeEntities(page.title.rendered)}</div>
                <div
                  className={classes.desc}
                  dangerouslySetInnerHTML={{ __html: page.content.rendered }}
                />
                {page.download_link_url && (
                  <BtnLink
                    link={page.download_link_url}
                    label={strings.pdf_label}
                    theme="red"
                    icon="download"
                  />
                )}
                {(page.ecwid_prodotto_id && mediaQueryKey > 1) && renderControls()}
              </div>
            </div>
          </div>
        </div>
        <div className={classes.gridImage}>
          <img src={getImageUrl(page.featured_media, 'full', stub)} alt={page.title.rendered} />
        </div>
        {(page.ecwid_prodotto_id && mediaQueryKey <= 1) && renderControls()}
      </div>
    )
  }

  /*------------------------------
  Render Related Products
  ------------------------------*/
  const renderRelatedProducts = () => {
    return relatedProduct.length > 0 ? (
      <div className={classes.products}>
        <h3>{strings.related_products_label}</h3>
        <ul className={classes.productView}>
          {relatedProduct
            .slice(0, 4)
            .map((item) => ({
              ...item,
              title: item.name,
              link: `${constants.urls.SITE}${window.pathSite}shop/${item.id}`,
              designer: categories.find((cat) => cat.id === item.categoryIds[0])?.name,
              image: item.originalImageUrl,
              price: item.defaultDisplayedPriceFormatted,
              discountPrice: item.compareToPriceFormatted,
              isInStock: item.inStock,
            })).map((p) => (
              <ProductItem
                key={p.id}
                item={p}
                shop
                onAddCartClick={() => handleAddToCart(p.id, 1)}
              />
            ))}
        </ul>
      </div>
    ) : null
  }

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderProduct()}
        {renderRelatedProducts()}
        <Footer />
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageProduct ${classes.root}`}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default Product
